/**
 * CDP Screencast Manager
 *
 * Owns one Chrome DevTools Protocol `Page.startScreencast` subscription per
 * Page and fans frames out to multiple subscribers at the JS layer. This
 * lets the live-streaming encoder (B) and the file-recording encoder (A)
 * share a single Chrome JPEG-encode pipeline rather than each opening their
 * own subscription.
 *
 * Per-subscriber state:
 * - independent `minIntervalMs` throttling
 * - choice of data-URL (`onFrame`) or raw-JPEG (`onJpeg`) delivery
 * - own contribution to the CDP-side params superset (quality, max dims,
 *   everyNthFrame, format)
 *
 * Subscriber lifecycle:
 * - first subscribe on a Page → `Page.startScreencast` (counts as one start)
 * - later subscribes with stricter params → stop+start with new superset
 * - last unsubscribe → `Page.stopScreencast`, detach session
 *
 * Frame ack stays exactly-once per CDP frame (Chrome blocks until ack).
 *
 * @module cdp-screencast
 * @see docs/browser-session.md
 */

import type { Page, CDPSession } from 'playwright';
import { consoleLogger, type BrowserLogger } from './logger.js';
import {
  isScreencastTraceEnabled,
  traceDigest,
  traceScreencast,
} from './screencast-trace.js';

/**
 * Interface for video encoding integration.
 * Implemented by apps/api's ScreencastEncoder (ffmpeg-based).
 * Optional — when not provided, video encoding is unavailable.
 */
export interface IScreencastEncoder {
  start(): void;
  stop(): Promise<void>;
  pushFrame(jpegBuffer: Buffer): void;
}

/**
 * Factory to create an encoder + check availability.
 * Injected by the host environment (e.g., apps/api provides ffmpeg-based encoder).
 */
export interface ScreencastEncoderFactory {
  isAvailable(): Promise<boolean>;
  create(options: {
    framerate: number;
    onInitSegment: (data: Buffer) => void;
    onMediaSegment: (data: Buffer) => void;
    onError?: (error: Error) => void;
  }): IScreencastEncoder;
}

let _logger: BrowserLogger = consoleLogger;

/**
 * Set the logger used by the screencast manager.
 */
export function setCdpScreencastLogger(l: BrowserLogger): void {
  _logger = l;
}

const getLogger = () => _logger;

export interface ScreencastOptions {
  /** Image format. Default: 'jpeg' */
  format?: 'jpeg' | 'png';
  /** Image quality (0-100). Default: 50 */
  quality?: number;
  /** Max width in pixels. Default: 1280 */
  maxWidth?: number;
  /** Max height in pixels. Default: 720 */
  maxHeight?: number;
  /** Minimum interval between delivered frames in ms. Default: 200 (~5fps) */
  minIntervalMs?: number;
  /** Deliver every Nth frame from CDP (1 = all). Default: 1 */
  everyNthFrame?: number;
  /**
   * Enable H.264 video encoding via ffmpeg.
   * When enabled, frames are piped to ffmpeg and output as fragmented MP4.
   * Falls back to data URL delivery if ffmpeg is unavailable.
   * Default: false
   */
  enableVideoEncoding?: boolean;
}

export type ScreencastFrameHandler = (dataUrl: string) => void | Promise<void>;

/** Frame metadata surfaced to subscribers, straight from CDP's frame metadata. */
export interface ScreencastFrameMetadata {
  /** Monotonic-ish timestamp from Chrome (seconds), if provided. */
  timestamp?: number;
  /**
   * Size of the surface Chrome actually captured, in CSS px. This is the only
   * report of the page's REAL viewport that reaches a frame consumer: a
   * consumer that sized itself from the requested viewport (the video encoder's
   * fixed canvas) can compare and complain instead of silently reconciling the
   * difference with padding.
   */
  deviceWidth?: number;
  deviceHeight?: number;
}

/** Callbacks for receiving encoded video segments */
export interface VideoEncodingCallbacks {
  onInitSegment: (data: Buffer) => void;
  onMediaSegment: (data: Buffer) => void;
  onError?: (error: Error) => void;
}

/**
 * Subscribe to the bus. At least one of `onJpeg`/`onFrame` must be set.
 */
export interface SubscribeOptions {
  /**
   * Raw JPEG fast path. Preferred for encoders that want bytes.
   *
   * The Buffer is shared across all `onJpeg` subscribers for a given frame —
   * **do not mutate, and do not retain past callback return**. Copy if you
   * need to hold the bytes (e.g. for async post-processing).
   *
   * Throwing/rejecting from the callback is logged at warn-level and isolated;
   * other subscribers still receive the frame and the bus does not unsubscribe
   * you on error. Surface your own errors if you need upstream signaling.
   */
  onJpeg?: (jpeg: Buffer, metadata: ScreencastFrameMetadata) => void;
  /**
   * Data-URL form (`data:image/jpeg;base64,...`). Used by snapshot/fallback
   * consumers. Same error-isolation policy as `onJpeg`.
   */
  onFrame?: ScreencastFrameHandler;
  /** Per-subscriber throttle. Default: 200ms (~5fps). */
  minIntervalMs?: number;
  /** CDP image format. Bus picks 'jpeg' if any subscriber wants it. Default: 'jpeg'. */
  format?: 'jpeg' | 'png';
  /** CDP JPEG quality. Bus picks the max across subscribers. Default: 50. */
  quality?: number;
  /** CDP max dimensions. Bus picks the max across subscribers. Default: 1280×720. */
  maxWidth?: number;
  maxHeight?: number;
  /** CDP everyNthFrame. Bus picks the min across subscribers. Default: 1. */
  everyNthFrame?: number;
}

export interface SubscriptionHandle {
  /** Detach from the bus; stops the CDP screencast if this was the last subscriber. */
  unsubscribe(): Promise<void>;
}

interface CdpParams {
  format: 'jpeg' | 'png';
  quality: number;
  maxWidth: number;
  maxHeight: number;
  everyNthFrame: number;
}

interface InternalSubscription {
  id: number;
  onJpeg?: (jpeg: Buffer, metadata: ScreencastFrameMetadata) => void;
  onFrame?: ScreencastFrameHandler;
  minIntervalMs: number;
  params: CdpParams;
  lastDeliveredAt: number;
  /**
   * Most recent throttle-dropped frame, held for trailing-edge delivery.
   * Stored as the raw base64 payload rather than a Buffer: the Buffer handed to
   * `onJpeg` is shared across subscribers and must not be retained, whereas the
   * base64 string already exists on the CDP event and costs nothing to keep.
   */
  pendingData: string | null;
  pendingMetadata: ScreencastFrameMetadata | null;
  /** Timer that will flush {@link pendingData}, or null when none is scheduled. */
  settleTimer: ReturnType<typeof setTimeout> | null;
}

const DEFAULT_PARAMS: CdpParams = {
  format: 'jpeg',
  quality: 50,
  maxWidth: 1280,
  maxHeight: 720,
  everyNthFrame: 1,
};

const DEFAULT_MIN_INTERVAL_MS = 200;

function resolveParams(opts: SubscribeOptions): CdpParams {
  return {
    format: opts.format ?? DEFAULT_PARAMS.format,
    quality: opts.quality ?? DEFAULT_PARAMS.quality,
    maxWidth: opts.maxWidth ?? DEFAULT_PARAMS.maxWidth,
    maxHeight: opts.maxHeight ?? DEFAULT_PARAMS.maxHeight,
    everyNthFrame: opts.everyNthFrame ?? DEFAULT_PARAMS.everyNthFrame,
  };
}

function paramsSuperset(subs: Iterable<InternalSubscription>): CdpParams {
  let result: CdpParams | null = null;
  for (const s of subs) {
    if (!result) {
      result = { ...s.params };
      continue;
    }
    // 'jpeg' wins ties — only fall to 'png' if every subscriber wants png.
    if (s.params.format === 'jpeg') result.format = 'jpeg';
    if (s.params.quality > result.quality) result.quality = s.params.quality;
    if (s.params.maxWidth > result.maxWidth) result.maxWidth = s.params.maxWidth;
    if (s.params.maxHeight > result.maxHeight) result.maxHeight = s.params.maxHeight;
    if (s.params.everyNthFrame < result.everyNthFrame) result.everyNthFrame = s.params.everyNthFrame;
  }
  return result ?? { ...DEFAULT_PARAMS };
}

function paramsEqual(a: CdpParams, b: CdpParams): boolean {
  return (
    a.format === b.format &&
    a.quality === b.quality &&
    a.maxWidth === b.maxWidth &&
    a.maxHeight === b.maxHeight &&
    a.everyNthFrame === b.everyNthFrame
  );
}

function isTargetClosedErrorMessage(message: string): boolean {
  return (
    message.includes('Target page, context or browser has been closed') ||
    message.includes('Target closed')
  );
}

/**
 * Manages CDP screencast for a single Playwright page.
 *
 * Two consumption modes are supported:
 * - **Subscribe API (preferred):** `subscribe(page, opts)` returns a handle.
 *   Multiple subscribers share one CDP session per page; first subscribe
 *   starts CDP, last unsubscribe stops it.
 * - **Legacy `start/stop` API:** retained for B's existing wiring; implemented
 *   internally as a single subscription plus optional encoder.
 */
export class CdpScreencastManager {
  private cdpSession: CDPSession | null = null;
  private currentPage: Page | null = null;
  private currentParams: CdpParams | null = null;
  /** Cached MIME framing for `data:` URLs; recomputed when params restart. */
  private currentMimeType = 'image/jpeg';
  private subscriptions = new Map<number, InternalSubscription>();
  private nextSubscriptionId = 1;
  /**
   * Single-flight queue. All state-mutating operations (`ensureRunningOn`,
   * `applyParams`, `restart`, `shutdown*`) chain off this so concurrent
   * subscribe/unsubscribe calls cannot race CDP session creation.
   */
  private opChain: Promise<unknown> = Promise.resolve();
  /**
   * Counts cold starts + page-swap restarts (one per `Page.startScreencast`
   * triggered by `subscribe()` against a different page or `restart()`).
   * Preserves the semantics of the prior `_screencastStartCount` field —
   * does NOT include param-change restarts (those are tracked separately).
   * Telemetry consumers reading `screencastStartCount` should read this.
   */
  private _pageStartCount = 0;
  /**
   * Counts param-change restarts: a stricter subscriber joining (or a
   * stricter subscriber leaving) caused stop+start with new superset params.
   * Surfaced separately so telemetry can distinguish unavoidable page churn
   * from param-thrash bugs.
   */
  private _paramRestartCount = 0;

  // Legacy `start/stop` state (single-handler compatibility path).
  private legacyOptions: Required<Omit<ScreencastOptions, 'enableVideoEncoding'>> & {
    enableVideoEncoding: boolean;
  };
  private legacySubscription: SubscriptionHandle | null = null;
  private legacyEncoderSubscription: SubscriptionHandle | null = null;
  private legacyEncoder: IScreencastEncoder | null = null;
  private _videoEncodingActive = false;
  private encoderFactory: ScreencastEncoderFactory | null = null;

  constructor(options?: ScreencastOptions, encoderFactory?: ScreencastEncoderFactory) {
    this.legacyOptions = {
      format: options?.format ?? DEFAULT_PARAMS.format,
      quality: options?.quality ?? DEFAULT_PARAMS.quality,
      maxWidth: options?.maxWidth ?? DEFAULT_PARAMS.maxWidth,
      maxHeight: options?.maxHeight ?? DEFAULT_PARAMS.maxHeight,
      minIntervalMs: options?.minIntervalMs ?? DEFAULT_MIN_INTERVAL_MS,
      everyNthFrame: options?.everyNthFrame ?? DEFAULT_PARAMS.everyNthFrame,
      enableVideoEncoding: options?.enableVideoEncoding ?? false,
    };
    this.encoderFactory = encoderFactory ?? null;
  }

  /** Whether video encoding is active (ffmpeg running) */
  get videoEncodingActive(): boolean {
    return this._videoEncodingActive;
  }

  get active(): boolean {
    return this.subscriptions.size > 0;
  }

  /**
   * Number of `Page.startScreencast` invocations from cold start + page-swap
   * restarts. Replaces the prior `_screencastStartCount` field on
   * PlaywrightClient with identical semantics — telemetry consumers tracking
   * front-gap latency vs. restart churn should keep using this number.
   *
   * Param-change restarts (stricter sub joined/left) are tracked separately
   * in `paramRestartCount` so they don't inflate the page-attach metric.
   */
  get startCount(): number {
    return this._pageStartCount;
  }

  /** See `startCount` — separated to avoid metric drift. */
  get paramRestartCount(): number {
    return this._paramRestartCount;
  }

  // ============================================================
  // Subscribe API (preferred)
  // ============================================================

  /**
   * Attach a subscriber. Starts the CDP screencast on `page` if this is the
   * first subscriber; if the merged params change, restarts CDP with the
   * new superset.
   *
   * The returned handle's `unsubscribe()` removes this subscriber and stops
   * CDP if no subscribers remain.
   *
   * Late-join semantics: subscribers receive only frames delivered AFTER
   * subscribe; the bus does not buffer prior frames.
   */
  async subscribe(page: Page, opts: SubscribeOptions): Promise<SubscriptionHandle> {
    if (!opts.onJpeg && !opts.onFrame) {
      throw new Error('subscribe(): one of onJpeg or onFrame is required');
    }

    const id = this.nextSubscriptionId++;
    const subscription: InternalSubscription = {
      id,
      onJpeg: opts.onJpeg,
      onFrame: opts.onFrame,
      minIntervalMs: opts.minIntervalMs ?? DEFAULT_MIN_INTERVAL_MS,
      params: resolveParams(opts),
      lastDeliveredAt: 0,
      pendingData: null,
      pendingMetadata: null,
      settleTimer: null,
    };

    await this.serialize(async () => {
      this.subscriptions.set(id, subscription);
      try {
        const running = await this.ensureRunningOn(page);
        if (!running) {
          this.subscriptions.delete(id);
        }
      } catch (err) {
        // Roll back so a failed start doesn't leave a phantom subscriber that
        // inflates paramsSuperset on the next subscribe attempt.
        this.subscriptions.delete(id);
        throw err;
      }
    });

    return {
      unsubscribe: async () => {
        await this.serialize(async () => {
          if (!this.subscriptions.has(id)) return;
          // Cancel any armed settle timer before dropping the subscription so a
          // detached subscriber can never receive a trailing frame.
          this.clearSettle(subscription);
          this.subscriptions.delete(id);
          if (this.subscriptions.size === 0) {
            await this.shutdownSessionOnly();
            return;
          }
          // Re-evaluate params; a stricter sub may have left, allowing relaxation.
          const page = this.currentPage;
          if (!page) return;
          await this.applyParams(page);
        });
      },
    };
  }

  /**
   * Move all live subscriptions to a new Page. The CDP session on the old
   * page is torn down; the screencast is restarted on the new page with the
   * same superset of params. Subscribers' callbacks and throttle state are
   * preserved (`lastDeliveredAt` not reset — throttling is wall-clock).
   */
  async restart(newPage: Page): Promise<void> {
    await this.serialize(async () => {
      if (this.subscriptions.size === 0) return;
      await this.shutdownSessionOnly();
      await this.ensureRunningOn(newPage);
    });
  }

  /**
   * Serialize a state-mutating operation against all other state-mutating
   * operations. Each call chains off the previous to prevent CDP session
   * creation/teardown races under concurrent subscribe/unsubscribe.
   */
  private serialize<T>(fn: () => Promise<T>): Promise<T> {
    const next = this.opChain.then(fn, fn);
    // Swallow rejections on the chain itself so one failure doesn't poison
    // every subsequent op; callers still see the rejection on their `next`.
    this.opChain = next.catch(() => undefined);
    return next;
  }

  // ============================================================
  // Legacy start/stop API (preserved for B's existing wiring)
  // ============================================================

  /**
   * Start screencast on the given page (legacy single-handler API).
   *
   * Equivalent to `subscribe(page, { onFrame, ...legacyOptions })` plus an
   * optional encoder subscription. Existing callers (B) keep working without
   * change; new callers should prefer `subscribe()`.
   *
   * If `options` is provided, it overrides any constructor-time legacy options.
   * This matters because the manager may have been constructed by another
   * caller (A) with no options — `start()` must not silently inherit those
   * stale defaults when B has its own.
   */
  async start(
    page: Page,
    onFrame: ScreencastFrameHandler,
    videoCallbacks?: VideoEncodingCallbacks,
    options?: ScreencastOptions,
  ): Promise<void> {
    if (this.legacySubscription) {
      getLogger().debug('[CdpScreencast] Already active, stopping before restart');
      await this.stop();
    }

    if (options) {
      this.legacyOptions = {
        format: options.format ?? this.legacyOptions.format,
        quality: options.quality ?? this.legacyOptions.quality,
        maxWidth: options.maxWidth ?? this.legacyOptions.maxWidth,
        maxHeight: options.maxHeight ?? this.legacyOptions.maxHeight,
        minIntervalMs: options.minIntervalMs ?? this.legacyOptions.minIntervalMs,
        everyNthFrame: options.everyNthFrame ?? this.legacyOptions.everyNthFrame,
        enableVideoEncoding: options.enableVideoEncoding ?? this.legacyOptions.enableVideoEncoding,
      };
    }

    const baseOpts: SubscribeOptions = {
      onFrame,
      format: this.legacyOptions.format,
      quality: this.legacyOptions.quality,
      maxWidth: this.legacyOptions.maxWidth,
      maxHeight: this.legacyOptions.maxHeight,
      everyNthFrame: this.legacyOptions.everyNthFrame,
      minIntervalMs: this.legacyOptions.minIntervalMs,
    };

    this.legacySubscription = await this.subscribe(page, baseOpts);

    // Initialize encoder subscription if enabled and factory is available.
    // Unwind on any failure so we never leave a half-attached encoder behind.
    if (this.legacyOptions.enableVideoEncoding && videoCallbacks && this.encoderFactory) {
      try {
        const available = await this.encoderFactory.isAvailable();
        if (!available) {
          getLogger().warn('[CdpScreencast] Encoder not available, video encoding disabled');
          return;
        }
        const framerate = Math.round(1000 / this.legacyOptions.minIntervalMs);
        const encoder = this.encoderFactory.create({
          framerate,
          onInitSegment: videoCallbacks.onInitSegment,
          onMediaSegment: videoCallbacks.onMediaSegment,
          onError: (err) => {
            getLogger().warn('[CdpScreencast] Video encoder error', { error: err.message });
            this._videoEncodingActive = false;
            this.legacyEncoder = null;
            videoCallbacks.onError?.(err);
          },
        });
        encoder.start();
        const encoderSub = await this.subscribe(page, {
          onJpeg: (jpeg) => {
            try {
              encoder.pushFrame(jpeg);
            } catch (err) {
              getLogger().warn('[CdpScreencast] Failed to push frame to encoder', {
                error: err instanceof Error ? err.message : String(err),
              });
            }
          },
          minIntervalMs: this.legacyOptions.minIntervalMs,
          format: this.legacyOptions.format,
          quality: this.legacyOptions.quality,
          maxWidth: this.legacyOptions.maxWidth,
          maxHeight: this.legacyOptions.maxHeight,
          everyNthFrame: this.legacyOptions.everyNthFrame,
        });
        // Commit only after both encoder.start() and subscribe() succeeded.
        this.legacyEncoder = encoder;
        this.legacyEncoderSubscription = encoderSub;
        this._videoEncodingActive = true;
        getLogger().info('[CdpScreencast] Video encoding enabled', { framerate });
      } catch (err) {
        getLogger().warn('[CdpScreencast] Failed to attach video encoder; recording disabled', {
          error: err instanceof Error ? err.message : String(err),
        });
        // legacySubscription stays attached — onFrame data-URL delivery still works.
        this._videoEncodingActive = false;
      }
    }
  }

  /**
   * Tear down the legacy single-handler session (and any sibling subscribers
   * that were attached via `subscribe()` — the CDP screencast stops when the
   * last subscriber leaves).
   */
  async stop(): Promise<void> {
    if (this.legacyEncoderSubscription) {
      await this.legacyEncoderSubscription.unsubscribe();
      this.legacyEncoderSubscription = null;
    }
    if (this.legacyEncoder) {
      try {
        await this.legacyEncoder.stop();
      } catch (err) {
        getLogger().warn('[CdpScreencast] Encoder stop failed', {
          error: err instanceof Error ? err.message : String(err),
        });
      }
      this.legacyEncoder = null;
      this._videoEncodingActive = false;
    }
    if (this.legacySubscription) {
      await this.legacySubscription.unsubscribe();
      this.legacySubscription = null;
    }
    getLogger().debug('[CdpScreencast] Stopped');
  }

  // ============================================================
  // Internal lifecycle
  // ============================================================

  /**
   * @param reason 'cold' = no CDP session yet (or page changed); bumps
   *               `_pageStartCount`. 'param-change' = same page, params
   *               drifted; bumps `_paramRestartCount`.
   */
  private async ensureRunningOn(
    page: Page,
    reason: 'cold' | 'param-change' = 'cold',
  ): Promise<boolean> {
    // Page may have been closed between when subscribe was queued and now
    // (e.g. workflow tore down its session). Return a no-op subscription rather
    // than leaving a phantom subscriber that keeps the manager active forever.
    if (page.isClosed()) {
      getLogger().debug('[CdpScreencast] Page already closed, skipping CDP session', {
        reason,
      });
      if (this.currentPage === page) {
        await this.cleanupSession();
        this.currentPage = null;
        this.currentParams = null;
      }
      return false;
    }

    const targetParams = paramsSuperset(this.subscriptions.values());

    if (this.cdpSession && this.currentPage === page) {
      if (!this.currentParams || paramsEqual(this.currentParams, targetParams)) {
        return true; // already running with the right params
      }
      // Params changed — restart with the new superset (single tight stop+start).
      await this.shutdownSessionOnly();
      reason = 'param-change';
    } else if (this.cdpSession && this.currentPage !== page) {
      await this.shutdownSessionOnly();
    }

    try {
      this.cdpSession = await page.context().newCDPSession(page);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      // The page or its context closed between our isClosed() check and the
      // CDP attach round-trip. Same shutdown story — log quietly and bail.
      if (isTargetClosedErrorMessage(message)) {
        getLogger().debug(
          '[CdpScreencast] Target closed during CDP attach, skipping',
          { reason }
        );
        this.cdpSession = null;
        this.currentPage = null;
        this.currentParams = null;
        return false;
      }
      getLogger().warn('[CdpScreencast] Failed to create CDP session', {
        error: message,
      });
      this.cdpSession = null;
      // Propagate so the caller (subscribe) can roll back the subscription
      // registration. Otherwise we'd silently register a phantom subscriber
      // that would never receive frames.
      throw err instanceof Error ? err : new Error(String(err));
    }
    this.currentPage = page;

    this.cdpSession.on(
      'Page.screencastFrame',
      (params: {
        data: string;
        sessionId: number;
        metadata: { timestamp?: number; deviceWidth?: number; deviceHeight?: number };
      }) => {
        this.handleFrame(params);
      },
    );

    try {
      await this.cdpSession.send('Page.startScreencast', { ...targetParams });
      this.currentParams = targetParams;
      this.currentMimeType = targetParams.format === 'png' ? 'image/png' : 'image/jpeg';
      if (reason === 'param-change') {
        this._paramRestartCount++;
      } else {
        this._pageStartCount++;
      }
      getLogger().debug('[CdpScreencast] Started', {
        ...targetParams,
        reason,
        subscribers: this.subscriptions.size,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      getLogger().warn('[CdpScreencast] Failed to start screencast', {
        error: message,
      });
      await this.cleanupSession();
      this.currentPage = null;
      this.currentParams = null;
      if (isTargetClosedErrorMessage(message)) {
        return false;
      }
    }

    return true;
  }

  private async applyParams(page: Page): Promise<void> {
    const targetParams = paramsSuperset(this.subscriptions.values());
    if (this.currentParams && paramsEqual(this.currentParams, targetParams)) return;
    await this.shutdownSessionOnly();
    await this.ensureRunningOn(page, 'param-change');
  }

  /** Shut down the CDP session without removing subscribers. */
  private async shutdownSessionOnly(): Promise<void> {
    if (this.cdpSession) {
      try {
        await this.cdpSession.send('Page.stopScreencast');
      } catch {
        /* session may already be detached */
      }
      await this.cleanupSession();
    }
    this.currentPage = null;
    this.currentParams = null;
  }

  private handleFrame(params: {
    data: string;
    sessionId: number;
    metadata: { timestamp?: number; deviceWidth?: number; deviceHeight?: number };
  }): void {
    // Always ack — exactly once per CDP frame, regardless of subscriber count.
    // Chrome blocks until ack; this is load-bearing.
    this.ackFrame(params.sessionId);

    if (this.subscriptions.size === 0) return;

    const now = Date.now();
    let jpegBuffer: Buffer | null = null;
    let dataUrl: string | null = null;
    const metadata: ScreencastFrameMetadata = {
      timestamp: params.metadata.timestamp,
      deviceWidth: params.metadata.deviceWidth,
      deviceHeight: params.metadata.deviceHeight,
    };

    // Phase-0 diagnostics: digest once per CDP frame, shared across subscribers.
    // Both are no-ops (and allocate nothing) when tracing is off.
    const tracing = isScreencastTraceEnabled();
    let traceDigestValue: string | undefined;
    if (tracing) {
      jpegBuffer = Buffer.from(params.data, 'base64');
      traceDigestValue = traceDigest(jpegBuffer);
    }

    for (const sub of this.subscriptions.values()) {
      const sinceLastDeliveryMs = now - sub.lastDeliveredAt;
      const accepted = sinceLastDeliveryMs >= sub.minIntervalMs;
      if (tracing) {
        traceScreencast({
          kind: 'bus_frame',
          subscriberId: sub.id,
          accepted,
          sinceLastDeliveryMs,
          minIntervalMs: sub.minIntervalMs,
          digest: traceDigestValue,
          bytes: jpegBuffer?.byteLength,
        });
      }
      if (!accepted) {
        // Trailing-edge settle. Without this, a frame dropped here is gone for
        // good, and if the page then goes idle the encoder's gap-fill duplicates
        // the PREVIOUS (now stale) frame across the whole idle period — so a
        // 16ms highlight can occupy ~86% of the recording. Holding the dropped
        // frame and flushing it once the throttle window expires bounds that
        // error to a single interval. Latest-wins: a newer drop supersedes.
        sub.pendingData = params.data;
        sub.pendingMetadata = metadata;
        this.scheduleSettle(sub, now);
        continue;
      }

      // A fresher frame won; anything held for settling is now obsolete.
      this.clearSettle(sub);
      sub.lastDeliveredAt = now;

      if (sub.onJpeg) {
        if (!jpegBuffer) jpegBuffer = Buffer.from(params.data, 'base64');
        this.deliverJpeg(sub, jpegBuffer, metadata);
      }

      if (sub.onFrame) {
        if (!dataUrl) {
          dataUrl = `data:${this.currentMimeType};base64,${params.data}`;
        }
        this.deliverFrame(sub, dataUrl);
      }
    }
  }

  private deliverJpeg(
    sub: InternalSubscription,
    jpeg: Buffer,
    metadata: ScreencastFrameMetadata,
  ): void {
    if (!sub.onJpeg) return;
    try {
      sub.onJpeg(jpeg, metadata);
    } catch (err) {
      getLogger().warn('[CdpScreencast] onJpeg subscriber threw', {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  private deliverFrame(sub: InternalSubscription, dataUrl: string): void {
    if (!sub.onFrame) return;
    try {
      const result = sub.onFrame(dataUrl);
      if (result && typeof result.then === 'function') {
        result.then(undefined, (err: unknown) => {
          getLogger().warn('[CdpScreencast] onFrame subscriber rejected', {
            error: err instanceof Error ? err.message : String(err),
          });
        });
      }
    } catch (err) {
      getLogger().warn('[CdpScreencast] onFrame subscriber threw', {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  /**
   * Schedule the pending frame to flush when this subscriber's throttle window
   * expires. Deliberately anchored to `lastDeliveredAt + minIntervalMs` rather
   * than debounced off the newest drop: under continuous motion a debounce would
   * postpone delivery indefinitely, whereas this simply delivers at the normal
   * cadence. An already-scheduled timer is left alone — its deadline is correct
   * and only the payload needs to be the newest.
   */
  private scheduleSettle(sub: InternalSubscription, now: number): void {
    if (sub.settleTimer !== null) return;
    const delay = Math.max(0, sub.minIntervalMs - (now - sub.lastDeliveredAt));
    const timer = setTimeout(() => {
      sub.settleTimer = null;
      this.flushSettle(sub);
    }, delay);
    // Never keep the process alive for a diagnostic-grade trailing frame.
    if (typeof timer.unref === 'function') timer.unref();
    sub.settleTimer = timer;
  }

  private clearSettle(sub: InternalSubscription): void {
    if (sub.settleTimer !== null) {
      clearTimeout(sub.settleTimer);
      sub.settleTimer = null;
    }
    sub.pendingData = null;
    sub.pendingMetadata = null;
  }

  /** Deliver the held frame, if this subscriber is still attached. */
  private flushSettle(sub: InternalSubscription): void {
    const data = sub.pendingData;
    const metadata = sub.pendingMetadata ?? {};
    sub.pendingData = null;
    sub.pendingMetadata = null;
    // Unsubscribed (or the whole session torn down) while the timer was armed.
    if (data === null || !this.subscriptions.has(sub.id)) return;

    sub.lastDeliveredAt = Date.now();
    if (isScreencastTraceEnabled()) {
      traceScreencast({
        kind: 'bus_frame',
        subscriberId: sub.id,
        accepted: true,
        minIntervalMs: sub.minIntervalMs,
        label: 'settle',
      });
    }
    if (sub.onJpeg) {
      this.deliverJpeg(sub, Buffer.from(data, 'base64'), metadata);
    }
    if (sub.onFrame) {
      this.deliverFrame(sub, `data:${this.currentMimeType};base64,${data}`);
    }
  }

  private ackFrame(sessionId: number): void {
    if (!this.cdpSession) return;
    this.cdpSession.send('Page.screencastFrameAck', { sessionId }).catch(() => {
      // Session may be detached — ignore
    });
  }

  private async cleanupSession(): Promise<void> {
    if (this.cdpSession) {
      try {
        await this.cdpSession.detach();
      } catch {
        // Already detached
      }
      this.cdpSession = null;
    }
  }
}
